import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ISubjectsStepLayoutProps } from './subjects-step.models';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { AddTwoToneIcon, Anchor, Button, IconButton, Panel, XIcon } from '@keplerco/core';
import { SelectionPanel } from '../../../../components/panels/selection/selection.panel';
import { SubjectSelectionList } from './subjects-selection-list';
import { ExplainerFocusPanelLayout } from '../../../../widgets/layouts';
import { useAppActions, useAppState } from '../../../../overmind';
import { RatingAssessmentEmptyStateImage } from '../rating-assessment-empty-state.image';
import { ExitModal } from '../../../../components/modals/exit.modal';
import { PeerAssessmentRequest, PeerAssessmentResponse } from '../../../../overmind/actions';
import { PagePath } from '../../../../navigation/navigation.enums';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { AssessmentAssigneeListItemLayout } from '../../../../components/assessment/assessment-entity-list-item.layout';

export function SubjectsStepLayout({ isLoading, setIsLoading, assessmentSlug, onBack, onNext, ...props }: ISubjectsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [peerAssessment, setPeerAssessment] = useState<PeerAssessmentResponse>(props.peerAssessment);
  const [isExplainerPanelOpen, setIsExplainerPanelOpen] = React.useState(false);
  const [isSelectionPanelOpen, setIsSelectionPanelOpen] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaveDraft, setIsSaveDraft] = useState<boolean | null>(null);
  const [peerAssessmentRequest, setPeerAssessmentRequest] = useState<PeerAssessmentRequest | null>(null);

  useEffect(() => {
    if (isSaveDraft === null || !peerAssessmentRequest) return;

    if (isSaveDraft) {
      actions.dismissAllNotifications();
      actions.addNotification({
        id: crypto.randomUUID(),
        active: true,
        priority: NotificationPriority.Toast,
        toastIncludeIcon: true,
        type: NotificationType.Warn,
        title: 'This assessment is now a draft',
        message: 'You will need to schedule this once your changes are complete',
      });
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
    } else {
      onNext(peerAssessment);
    }
    setPeerAssessmentRequest(null);
    setIsSaveDraft(null);
    setIsLoading(false);
  }, [isSaveDraft, peerAssessmentRequest]);

  useEffect(() => {
    if (hasUnsavedChanges) return;
    setHasUnsavedChanges(JSON.stringify(peerAssessment.assignees) !== JSON.stringify(props.peerAssessment.assignees));
  }, [peerAssessment]);

  async function saveSubjects(isSaveDraft?: boolean) {
    setIsLoading(true);

    const request: PeerAssessmentRequest = {
      ...peerAssessment,
      assignees: peerAssessment.assignees.map(assignee => ({
        ...assignee,
        reviewers: assignee.reviewers.map(reviewer => ({ learnerSlug: reviewer.learnerSlug, isManager: reviewer.isManager })),
        companySkillIds: assignee.companySkills.map(companySkill => companySkill.id),
      })),
    };
    setPeerAssessmentRequest(request);
    await actions.updatePeerAssessment({ assessmentSlug, peerAssessment: request });
    
    setHasUnsavedChanges(false);
    setIsSaveDraft(isSaveDraft ?? false);
  }

  return (
    <React.Fragment>
      <div className={assessmentsWizardStyles.stepContainer}>
        <SkeletonOverlay isLoading={isLoading}>
          <WidgetCard
            title="Subjects"
            subtitle="The subjects will be assessed by their selected peers and managers (Assessors)"
            onHelpClick={() => setIsExplainerPanelOpen(true)}
            button={
              <Button type="button" square theme="dark" aria-label="Add subjects" onClick={() => setIsSelectionPanelOpen(true)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AddTwoToneIcon />
                  Add
                </div>
              </Button>
            }
          >
            {!!peerAssessment?.assignees.length ? (
              <React.Fragment>
                <h4>Selected subjects ({peerAssessment.assignees.length}):</h4>

                {peerAssessment.assignees?.map(assignee => (
                  <div key={assignee.learnerSlug} className={classNames(assessmentsWizardStyles.assessmentStepListItemGrid, 'card')} style={{ borderLeftColor: 'var(--light)', borderLeftWidth: 4, borderRadius: '0px 8px 8px 0px' }}>
                    <AssessmentAssigneeListItemLayout assignee={assignee}>
                      <IconButton iconType="fill" aria-label="Remove person from subjects" onClick={() => setPeerAssessment(currentState => ({ ...currentState, assignees: currentState.assignees.filter(a => a.learnerSlug !== assignee.learnerSlug) }))}>
                        <XIcon />
                      </IconButton>
                    </AssessmentAssigneeListItemLayout>
                  </div>
                ))}
              </React.Fragment>
            ) : (
              <EmptyState title="" subtitle="The selected subjects will display here" imageElement={<RatingAssessmentEmptyStateImage />} />
            )}
          </WidgetCard>
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor reverse arrow aria-label="Return to previous step" onClick={onBack}>
            Back
          </Anchor>

          <div className={assessmentsWizardStyles.stepFooterButtonGroup}>
            <Anchor disabled={isLoading} aria-label="Save as draft and exit" onClick={() => saveSubjects(true)}>
              Save draft
            </Anchor>

            <Button disabled={isLoading} filled type="button" arrow aria-label="Save and continue to next step" onClick={() => saveSubjects()}>
              Next
            </Button>
          </div>
        </footer>
      </div>

      <Panel open={!!isExplainerPanelOpen} onClose={() => setIsExplainerPanelOpen(false)}>
        <ExplainerFocusPanelLayout title="Understanding assessment subjects" subtitle="Subjects are the people whose skills are being assessed" onClose={() => setIsExplainerPanelOpen(false)}>
          <p>A subject is anyone in your organisation selected to take part in a skills assessment.</p>
          <p>Subjects receive scores and comments from assessors, covering individual skills and overall performance.</p>
          <br />
          <p>Subjects do not assess others—the process is designed to gather focused, one-way feedback to support their development.</p>
        </ExplainerFocusPanelLayout>
      </Panel>

      <Panel open={isSelectionPanelOpen} onClose={() => setIsSelectionPanelOpen(false)} size="fullscreen">
        {isSelectionPanelOpen && (
          <SelectionPanel
            title="Subjects of assessment"
            subtitle="Select who you'd like to be assessed in this assessment"
            selectedEntities={peerAssessment.assignees.map(assignee => ({
              slug: assignee.learnerSlug,
              name: assignee.fullName,
              entity: assignee,
            }))}
            entityType="subjects"
            onCancel={() => setIsSelectionPanelOpen(false)}
            onAdd={selectedEntities => {
              setPeerAssessment(currentState => ({ ...currentState, assignees: selectedEntities.map(selectedEntity => selectedEntity.entity) }));
              setIsSelectionPanelOpen(false);
            }}
          >
            {({ selectedEntities, setSelectedEntities }) => <SubjectSelectionList selectedEntities={selectedEntities} setSelectedEntities={setSelectedEntities} />}
          </SelectionPanel>
        )}
      </Panel>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </React.Fragment>
  );
}
